import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=authed
  const [error, setError] = useState("");
  const i18n = useI18n();

  const bootstrap = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      i18n?.syncFromUser?.(data);
    } catch {
      setUser(false);
    }
  }, [i18n]);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password) => {
    try {
      setError("");
      await api.post("/auth/login", { email, password });
      const { data } = await api.get("/auth/me");
      setUser(data);
      i18n?.syncFromUser?.(data);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const can = (perm) => {
    if (!user) return false;
    if (user.role === "admin") return true;
    const perms = user.permissions || [];
    if (perms.includes("*")) return true;
    if (perms.includes(perm)) return true;
    const res = perm.split(":")[0];
    return perms.includes(`${res}:*`);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, error, can }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
